import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Hero } from "../components/Hero";
import { QuickForm } from "../components/QuickForm";
import { AuthorityRibbon } from "../components/AuthorityRibbon";
import { ProblemSolution } from "../components/ProblemSolution";
import { Treatments } from "../components/Treatments";
import { HomePrices } from "../components/HomePrices";
import { Reviews } from "../components/Reviews";
import { VideoShowcase } from "../components/VideoShowcase";
import { Differentials } from "../components/Differentials";
import { DoctorBlock } from "../components/DoctorBlock";
import { SectionEyebrow } from "../components/SectionEyebrow";
import { buildWhatsAppUrl, trackWhatsAppClick } from "../lib/whatsapp";
import { WhatsappLogo } from "@phosphor-icons/react";
import { useSeo, SITE_URL } from "../hooks/useSeo";
import { useJsonLd } from "../hooks/useJsonLd";
import { buildMedicalClinicLd, buildFaqLd, buildPhysicianLd } from "../lib/seo";

const FaqPreview = () => {
  const { t } = useTranslation();
  const items = (t("faq.items", { returnObjects: true }) || []).slice(0, 5);
  const [open, setOpen] = useState(0);
  return (
    <section id="faq-home" data-testid="faq-home-section" className="py-24 lg:py-36 bg-[#F6F5F2]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <SectionEyebrow num="08">{t("faq.eyebrow")}</SectionEyebrow>
          <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl font-light tracking-tight leading-[1.05] text-[#3E5239]">
            {t("faq.title_l1")}
            <br />
            <span className="italic text-[#A7CA99]">{t("faq.title_l2")}</span>
          </h2>
          <a
            href={buildWhatsAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackWhatsAppClick("faq_preview_cta")}
            data-testid="faq-whatsapp-cta"
            className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] hover:bg-[#1DA851] text-white text-xs uppercase tracking-[0.22em] transition-colors"
          >
            <WhatsappLogo size={16} weight="fill" />
            {t("faq.cta_whatsapp", "Conversar no WhatsApp")}
          </a>
        </div>
        <div className="lg:col-span-7">
          {items.map((it, i) => (
            <div key={it.q} className="border-b border-[#E4E2DA]" data-testid={`home-faq-${i}`}>
              <button type="button" onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-start justify-between gap-6 py-5 text-left group">
                <span className="font-serif text-lg lg:text-xl font-light text-[#3E5239] group-hover:text-[#A7CA99] transition-colors">{it.q}</span>
                <span className={`text-[#A7CA99] text-xl shrink-0 mt-1 transition-transform duration-300 ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              <div className={`grid transition-all duration-300 ${open === i ? "grid-rows-[1fr] opacity-100 pb-5" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <p className="text-[#6B7A66] font-light leading-relaxed text-sm">{it.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  useSeo({
    title: t("seo.home_title"),
    description: t("seo.home_description"),
    canonicalPath: "/",
    image: `${SITE_URL}/clinic-reception.jpg`,
  });

  const homeFaqItems = (t("faq.items", { returnObjects: true }) || []).slice(0, 5);
  const ldData = useMemo(
    () => [buildMedicalClinicLd(), buildPhysicianLd(), buildFaqLd(homeFaqItems)].filter(Boolean),
    [homeFaqItems]
  );
  useJsonLd(ldData, "home");

  return (
    <>
      <Hero />
      <QuickForm />
      <AuthorityRibbon />
      <ProblemSolution />
      <Treatments />
      <HomePrices />
      <Reviews />
      <VideoShowcase />
      <Differentials />
      <DoctorBlock />
      <FaqPreview />
    </>
  );
};

export default HomePage;
